/**
 * Document Processing Status Hook
 *
 * Subscribes to real-time processing statuses for multiple documents via Convex.
 * Used by the upload sheet and document detail to show live processing progress.
 */

import { api } from "convex/_generated/api";
import { useMemo } from "react";
import type { Id } from "@/packages/types";
import { useWorkspaceQuery } from "@/hooks";

export type ProcessingStepStatus =
  | "pending"
  | "generating"
  | "completed"
  | "failed"
  | "retrying"
  | undefined;

export interface ProcessingStatus {
  documentId: Id<"documents">;
  summaryStatus: ProcessingStepStatus;
  tagGenerationStatus: ProcessingStepStatus;
  suggestedChecklistItemsStatus: ProcessingStepStatus;
  isProcessing: boolean;
}

export function useDocumentProcessingStatus(documentIds: Id<"documents">[]): {
  statuses: Map<Id<"documents">, ProcessingStatus>;
  isLoading: boolean;
} {
  const { data, isLoading } = useWorkspaceQuery(
    api.document_queries.getProcessingStatuses,
    { documentIds },
    { enabled: documentIds.length > 0 },
  );

  const statuses = useMemo(() => {
    const map = new Map<Id<"documents">, ProcessingStatus>();
    if (!data) return map;
    for (const item of data) {
      map.set(item.documentId, item as ProcessingStatus);
    }
    return map;
  }, [data]);

  return { statuses, isLoading };
}

export function useDocumentProcessingStatusSingle(
  documentId: Id<"documents"> | undefined,
): {
  status: ProcessingStatus | undefined;
  isLoading: boolean;
} {
  const ids = useMemo(() => (documentId ? [documentId] : []), [documentId]);

  const { statuses, isLoading } = useDocumentProcessingStatus(ids);

  return {
    status: documentId ? statuses.get(documentId) : undefined,
    isLoading,
  };
}
